import React, { useState } from 'react';
import SafeIcon from '../../../common/SafeIcon';
import { useCRM } from '../../../context/CRMContext';
import { motion, AnimatePresence } from 'framer-motion';

export default function WorkspaceSection({ project }) {
  const { projectWorkspaces, currentUser, users, addWorkspaceMessage, addWorkspaceNote, toggleWorkspaceTask } = useCRM();
  const workspaces = projectWorkspaces || {};
  const workspace = workspaces[project.id] || { messages: [], notes: [], tasks: [] };
  const [newMessage, setNewMessage] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNote, setNewNote] = useState({ title: '', content: '' });

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    addWorkspaceMessage(project.id, { user_id: currentUser.user_id, text: newMessage });
    setNewMessage('');
  };

  const handleAddNote = () => {
    if (!newNote.title.trim()) return;
    addWorkspaceNote(project.id, { ...newNote, updated_by: currentUser.user_id });
    setNewNote({ title: '', content: '' });
    setIsAddingNote(false);
  };

  const getUser = (id) => users.find(u => u.user_id === id) || { name: 'Ukjent' };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 h-[calc(100vh-280px)]">
      {/* Messages / Chat */}
      <div className="lg:col-span-2 flex flex-col bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="p-4 border-b border-slate-100 flex items-center gap-2 bg-slate-50/50">
          <SafeIcon icon="💬" className="text-primary-600" />
          <h3 className="font-bold text-slate-800">Prosjektchat</h3>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {workspace.messages.map((msg, idx) => {
            const user = getUser(msg.user_id);
            const isMe = msg.user_id === currentUser.user_id;
            return (
              <div key={idx} className={`flex gap-3 ${isMe ? 'flex-row-reverse' : ''}`}>
                <img src={user.avatar} className="w-8 h-8 rounded-full object-cover border border-slate-200" alt="" />
                <div className={`max-w-[70%] ${isMe ? 'items-end' : ''}`}>
                  <div className={`p-3 rounded-2xl text-sm ${isMe ? 'bg-primary-600 text-white rounded-tr-none' : 'bg-slate-100 text-slate-800 rounded-tl-none'}`}>
                    {msg.text}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1 px-1">
                    {user.name} • {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-100 flex gap-2">
          <input type="text" value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Skriv en melding til teamet..." className="flex-1 bg-slate-50 border-slate-200 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-primary-500/20 outline-none" />
          <button type="submit" className="p-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors">
            <SafeIcon icon="➡️" />
          </button>
        </form>
      </div>

      {/* Sidebar: Tasks & Notes */}
      <div className="flex flex-col gap-8 overflow-y-auto pr-1">
        {/* Quick Tasks */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
            <div className="flex items-center gap-2">
              <SafeIcon icon="✅" className="text-emerald-600" />
              <h3 className="font-bold text-slate-800">Team Oppgaver</h3>
            </div>
          </div>
          <div className="p-4 space-y-2">
            {workspace.tasks.map(task => (
              <button key={task.id} onClick={() => toggleWorkspaceTask(project.id, task.id)} className="w-full flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg transition-colors text-left group">
                <SafeIcon icon={task.status === 'completed' ? "✅" : "⭕"} className={task.status === 'completed' ? 'text-emerald-500' : 'text-slate-300 group-hover:text-primary-400'} />
                <span className={`text-sm ${task.status === 'completed' ? 'text-slate-400 line-through' : 'text-slate-700 font-medium'}`}>
                  {task.title}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Shared Notes */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex-1">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
            <div className="flex items-center gap-2">
              <SafeIcon icon="📝" className="text-amber-600" />
              <h3 className="font-bold text-slate-800">Delt Notatblokk</h3>
            </div>
            <button onClick={() => setIsAddingNote(true)} className="p-1 hover:bg-slate-200 rounded transition-colors text-slate-600">
              <SafeIcon icon="➕" />
            </button>
          </div>
          <div className="p-4 space-y-4">
            <AnimatePresence>
              {isAddingNote && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="p-3 bg-amber-50 rounded-xl border border-amber-100 space-y-3">
                  <input type="text" placeholder="Tittel..." className="w-full bg-transparent border-0 border-b border-amber-200 text-sm font-bold focus:ring-0 px-0" value={newNote.title} onChange={(e) => setNewNote({ ...newNote, title: e.target.value })} />
                  <textarea placeholder="Innhold..." rows="3" className="w-full bg-transparent border-0 text-xs focus:ring-0 px-0 resize-none" value={newNote.content} onChange={(e) => setNewNote({ ...newNote, content: e.target.value })} />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setIsAddingNote(false)} className="text-[10px] font-bold text-slate-500 uppercase">Avbryt</button>
                    <button onClick={handleAddNote} className="text-[10px] font-bold text-primary-600 uppercase">Lagre</button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            {workspace.notes.map(note => (
              <div key={note.id} className="p-3 bg-slate-50 rounded-xl border border-slate-200 hover:border-primary-200 transition-colors">
                <h4 className="text-sm font-bold text-slate-800 mb-1">{note.title}</h4>
                <p className="text-xs text-slate-600 line-clamp-2 mb-2 leading-relaxed">{note.content}</p>
                <div className="flex items-center justify-between text-[10px] text-slate-400">
                  <div className="flex items-center gap-1">
                    <SafeIcon icon="👤" className="w-2.5 h-2.5" /> {getUser(note.updated_by).name}
                  </div>
                  <div className="flex items-center gap-1">
                    <SafeIcon icon="🕒" className="w-2.5 h-2.5" /> {new Date(note.updated_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}